import { useEffect, useMemo, useState } from 'react';
import {
    Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, MenuItem, Stack,
    Switch, SvgIcon, TextField, Typography, CircularProgress, Tooltip
} from '@mui/material';
import { put } from 'aws-amplify/api';
import { toast } from 'react-toastify';
import { ulid } from 'ulidx';
import { ReactComponent as CrossIcon } from '../../../icons/cross.svg';
import { ReactComponent as TrashIcon } from '../../../icons/trash.svg';
import {
    AudioFile, AudioFileCategory, AudioFileType, AudioFileVersion
} from '@aviation/cabinapp-cabincast-sdk';
import { AUDIO_FILE_CATEGORIES, AUDIO_FILE_LANGUAGES, DEFAULT_LANGUAGE, uploadAudioFileVersion } from './AudioFile';

export interface IAudioFileUploadDialogProps {
    open: boolean;
    files: File[];
    onClose: () => void;
    onSaved: () => void;
}

interface IUploadItem {
    key: string;
    file: File;
    language: string;
}

const nextFreeLanguage = (used: string[]) =>
    AUDIO_FILE_LANGUAGES.find(l => !used.includes(l.code))?.code ?? DEFAULT_LANGUAGE;

const createItems = (files: File[]): IUploadItem[] => {
    const used: string[] = [];
    return files.map((file, index) => {
        const language = index === 0 ? DEFAULT_LANGUAGE : nextFreeLanguage(used);
        used.push(language);
        return { key: ulid(), file, language };
    });
};

async function putJson<T>(path: string, body: any): Promise<T | undefined> {
    const operation = put({ path, apiName: 'api', options: { body } });
    const response = await operation.response;
    try {
        return await response.body.json() as unknown as T;
    } catch {
        return undefined; // empty response body
    }
}

function AudioFileUploadDialog({ open, files, onClose, onSaved }: IAudioFileUploadDialogProps) {
    const [items, setItems] = useState<IUploadItem[]>([]);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [type, setType] = useState<AudioFileType>('announcement');
    const [category, setCategory] = useState<AudioFileCategory>('safety');
    const [validFrom, setValidFrom] = useState('');
    const [validTo, setValidTo] = useState('');
    const [enabled, setEnabled] = useState(false);
    const [saving, setSaving] = useState(false);
    const [touched, setTouched] = useState(false);

    useEffect(() => {
        if (!open) return;
        setItems(createItems(files));
        setTitle('');
        setDescription('');
        setType('announcement');
        setCategory('safety');
        setValidFrom('');
        setValidTo('');
        setEnabled(false);
        setTouched(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Files dropped while the dialog is already open are appended.
    useEffect(() => {
        if (!open) return;
        setItems(current => {
            const known = new Set(current.map(i => i.file));
            const added = files.filter(f => !known.has(f));
            if (added.length === 0) return current;
            const used = current.map(i => i.language);
            return [...current, ...added.map(file => {
                const language = nextFreeLanguage(used);
                used.push(language);
                return { key: ulid(), file, language };
            })];
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [files]);

    const handleTypeChange = (value: AudioFileType) => {
        setType(value);
        setCategory(value === 'music' ? 'music' : (category === 'music' ? 'safety' : category));
    };

    const errors = useMemo(() => {
        const result: string[] = [];
        if (items.length === 0) result.push('Select at least one file.');
        const languages = items.map(i => i.language);
        if (items.length > 0 && !languages.includes(DEFAULT_LANGUAGE)) result.push('One file must be marked as English.');
        if (new Set(languages).size !== languages.length) result.push('Each language can only be used once.');
        if (validFrom && validTo && validTo < validFrom) result.push('"Valid to" must not be before "Valid from".');
        return result;
    }, [items, validFrom, validTo]);

    const titleMissing = title.trim() === '';
    const canSave = !titleMissing && errors.length === 0 && !saving;

    const handleSave = async () => {
        setTouched(true);
        if (!canSave) return;

        setSaving(true);
        try {
            const id = ulid();
            const versions: AudioFileVersion[] = await Promise.all(
                items.map(item => uploadAudioFileVersion(id, item.file, item.language)));

            const audioFile: AudioFile = {
                Id: id,
                Title: title.trim(),
                Description: description.trim() || undefined,
                Type: type,
                Category: type === 'music' ? 'music' : category,
                Versions: versions,
                Languages: versions.map(v => v.Language!),
                ValidFrom: validFrom || undefined,
                ValidTo: validTo || undefined,
                Enabled: enabled,
            };
            await putJson('/api/audiofiles/', audioFile);

            toast.success('Audio file saved successfully.');
            onSaved();
        } catch (e) {
            console.error(e);
            toast.error('An error occurred while saving the audio file.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth='sm'>
            <DialogTitle sx={{ m: 0, p: 2 }}>
                New audio file
                <IconButton aria-label='close' disabled={saving} onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}>
                    <SvgIcon component={CrossIcon} inheritViewBox />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2} sx={{ pt: 1 }}>
                    <TextField label='Title' required size='small' value={title} onChange={e => setTitle(e.target.value)}
                        error={touched && titleMissing} helperText={touched && titleMissing ? 'Title is required' : undefined} />
                    <TextField label='Description' size='small' multiline minRows={2} value={description} onChange={e => setDescription(e.target.value)} />
                    <Stack direction='row' spacing={2}>
                        <TextField select fullWidth label='Type' size='small' value={type} onChange={e => handleTypeChange(e.target.value as AudioFileType)}>
                            <MenuItem value='announcement'>Announcement</MenuItem>
                            <MenuItem value='music'>Music</MenuItem>
                        </TextField>
                        <TextField select fullWidth label='Category' size='small' value={type === 'music' ? 'music' : category}
                            disabled={type !== 'announcement'} onChange={e => setCategory(e.target.value as AudioFileCategory)}>
                            {AUDIO_FILE_CATEGORIES.filter(c => type === 'music' ? c.value === 'music' : c.value !== 'music').map(c =>
                                <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                        </TextField>
                    </Stack>
                    <Stack direction='row' spacing={2}>
                        <TextField fullWidth type='date' label='Valid from' size='small' value={validFrom} onChange={e => setValidFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
                        <TextField fullWidth type='date' label='Valid to' size='small' value={validTo} onChange={e => setValidTo(e.target.value)} InputLabelProps={{ shrink: true }}
                            inputProps={{ min: validFrom || undefined }} />
                    </Stack>
                    <FormControlLabel control={<Switch checked={enabled} onChange={e => setEnabled(e.target.checked)} />} label='Enabled' />

                    <Typography variant='subtitle2'>Files</Typography>
                    {items.map(item => (
                        <Stack key={item.key} direction='row' spacing={1} alignItems='center'>
                            <Typography noWrap sx={{ flexGrow: 1, minWidth: 0 }} title={item.file.name}>{item.file.name}</Typography>
                            <TextField select size='small' sx={{ width: 150 }} value={item.language}
                                onChange={e => setItems(items.map(i => i.key === item.key ? { ...i, language: e.target.value } : i))}>
                                {AUDIO_FILE_LANGUAGES.map(l => <MenuItem key={l.code} value={l.code}>{l.label} ({l.code})</MenuItem>)}
                            </TextField>
                            <Tooltip title='Remove file'>
                                <span>
                                    <IconButton color='error' disabled={saving} onClick={() => setItems(items.filter(i => i.key !== item.key))}>
                                        <SvgIcon component={TrashIcon} inheritViewBox />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Stack>
                    ))}
                    {errors.map(e => <Alert key={e} severity='error'>{e}</Alert>)}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>Cancel</Button>
                <Button variant='contained' onClick={handleSave} disabled={saving || errors.length > 0}
                    startIcon={saving ? <CircularProgress size={16} color='inherit' /> : undefined}>Save</Button>
            </DialogActions>
        </Dialog>
    );
}

export default AudioFileUploadDialog;
